import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Receipt as InvoiceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  clearError,
} from '../../store/slices/customerSlice';
import { Customer } from '../../types/invoice';
import CustomerForm, { CustomerFormData } from './CustomerForm';

const Customers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { customers, loading, error } = useSelector((state: RootState) => state.customers);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicateCustomer, setDuplicateCustomer] = useState<Customer | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCustomers(undefined));
  }, [dispatch]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      dispatch(fetchCustomers(value || undefined));
    },
    [dispatch]
  );

  const handleCreate = async (data: CustomerFormData) => {
    setFormError(null);
    setDuplicateCustomer(null);
    const result = await dispatch(createCustomer(data));
    if (createCustomer.fulfilled.match(result)) {
      setCreateOpen(false);
    } else {
      const payload = result.payload as any;
      setFormError(payload?.error || 'Nepodařilo se vytvořit dodavatele');
      setDuplicateCustomer(payload?.existing || null);
    }
  };

  const handleUpdate = async (data: CustomerFormData) => {
    if (!editCustomer) return;
    setFormError(null);
    setDuplicateCustomer(null);
    const result = await dispatch(updateCustomer({ id: editCustomer.id, data }));
    if (updateCustomer.fulfilled.match(result)) {
      setEditCustomer(null);
    } else {
      const payload = result.payload as any;
      setFormError(payload?.error || 'Nepodařilo se upravit dodavatele');
      setDuplicateCustomer(payload?.existing || null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    await dispatch(deleteCustomer(deleteTarget.id));
    setDeleteLoading(false);
    setDeleteTarget(null);
  };

  const filtered = customers.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      (c.ico && c.ico.toLowerCase().includes(q)) ||
      (c.dic && c.dic.toLowerCase().includes(q))
    );
  });

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Adresář dodavatelů
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Hledat..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 220 }}
          />
          <Tooltip title="Obnovit">
            <IconButton onClick={() => dispatch(fetchCustomers(undefined))} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormError(null);
              setDuplicateCustomer(null);
              setCreateOpen(true);
            }}
          >
            Nový dodavatel
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Název</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>IČO</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>DIČ</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Číslo účtu</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Adresa</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Akce
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Žádný dodavatel neodpovídá hledání' : 'Zatím žádní dodavatelé'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{customer.name}</TableCell>
                    <TableCell>{customer.ico || '—'}</TableCell>
                    <TableCell>{customer.dic || '—'}</TableCell>
                    <TableCell>
                      {customer.bankAccount ? (
                        <Chip
                          label={
                            customer.bankCode && !customer.bankAccount.includes('/')
                              ? `${customer.bankAccount}/${customer.bankCode}`
                              : customer.bankAccount
                          }
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {customer.address || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Zobrazit faktury">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/invoices?customerId=${customer.id}`)}
                        >
                          <InvoiceIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Upravit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormError(null);
                            setDuplicateCustomer(null);
                            setEditCustomer(customer);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Smazat">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(customer)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create dialog */}
      <CustomerForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        title="Nový dodavatel"
        submitLabel="Vytvořit"
        errorMessage={formError}
        duplicateCustomer={duplicateCustomer}
      />

      {/* Edit dialog */}
      {editCustomer && (
        <CustomerForm
          open={!!editCustomer}
          onClose={() => setEditCustomer(null)}
          onSubmit={handleUpdate}
          initialData={{
            name: editCustomer.name,
            ico: editCustomer.ico || '',
            dic: editCustomer.dic || '',
            address: editCustomer.address || '',
            bankAccount: editCustomer.bankAccount
              ? editCustomer.bankCode && !editCustomer.bankAccount.includes('/')
                ? `${editCustomer.bankAccount}/${editCustomer.bankCode}`
                : editCustomer.bankAccount
              : '',
            email: editCustomer.email || '',
            phone: editCustomer.phone || '',
            notes: editCustomer.notes || '',
          }}
          title={`Upravit: ${editCustomer.name}`}
          submitLabel="Uložit změny"
          errorMessage={formError}
          duplicateCustomer={duplicateCustomer}
        />
      )}

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Smazat dodavatele</DialogTitle>
        <DialogContent>
          <Typography>
            Opravdu chcete smazat dodavatele <strong>{deleteTarget?.name}</strong>? Faktury
            propojené s tímto dodavatelem budou odznačeny.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Zrušit
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Smazat'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
